import React, { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '../../../components/layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { womensHealthApi } from '../../../lib/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Search, Loader2, AlertTriangle, Clock, CheckCircle, FileText,
  Filter, ChevronRight, Activity, Eye
} from 'lucide-react';

const STATUS_MAP = {
  draft: { label: 'Taslak', color: 'bg-slate-100 text-slate-600' },
  in_progress: { label: 'Devam Ediyor', color: 'bg-blue-100 text-blue-700' },
  awaiting_review: { label: 'Degerlendirme Bekliyor', color: 'bg-amber-100 text-amber-700' },
  reviewed: { label: 'Degerlendirildi', color: 'bg-green-100 text-green-700' },
  closed: { label: 'Kapandi', color: 'bg-slate-200 text-slate-500' },
};

const TYPE_MAP = {
  appearance_tracking: 'Gorunum Takibi',
  discharge_triage: 'Semptom Triaji',
  followup: 'Takip',
  general: 'Genel',
};

const SEVERITY_COLORS = {
  low: 'bg-blue-100 text-blue-700',
  medium: 'bg-amber-100 text-amber-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700',
};

export default function EncountersListPage() {
  const navigate = useNavigate();
  const [encounters, setEncounters] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  const fetchEncounters = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (typeFilter !== 'all') params.encounter_type = typeFilter;
      if (search) params.search = search;
      const data = await womensHealthApi.listEncounters(params);
      setEncounters(data.encounters || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error('Vakalar yuklenirken hata olustu');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter, search]);

  useEffect(() => { fetchEncounters(); }, [fetchEncounters]);

  return (
    <DashboardLayout title="Kadin Sagligi" subtitle="Klinik degerlendirme vakalari">
      <div className="space-y-4">
        {/* Actions Bar */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Hasta ara..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 w-48"
                data-testid="wh-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40" data-testid="wh-status-filter">
                <Filter className="w-3.5 h-3.5 mr-1.5" />
                <SelectValue placeholder="Durum" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tum Durumlar</SelectItem>
                <SelectItem value="draft">Taslak</SelectItem>
                <SelectItem value="in_progress">Devam Ediyor</SelectItem>
                <SelectItem value="awaiting_review">Degerlendirme Bekliyor</SelectItem>
                <SelectItem value="reviewed">Degerlendirildi</SelectItem>
                <SelectItem value="closed">Kapandi</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-40" data-testid="wh-type-filter">
                <SelectValue placeholder="Tur" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tum Turler</SelectItem>
                <SelectItem value="appearance_tracking">Gorunum Takibi</SelectItem>
                <SelectItem value="discharge_triage">Semptom Triaji</SelectItem>
                <SelectItem value="followup">Takip</SelectItem>
                <SelectItem value="general">Genel</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={() => navigate('/doctor/womens-health/new')}
            className="bg-[#5A67D8] hover:bg-[#4C51BF]"
            data-testid="wh-new-encounter-btn"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            Yeni Degerlendirme
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                <FileText className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Toplam</p>
                <p className="text-lg font-bold text-slate-800">{total}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center">
                <Clock className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Bekleyen</p>
                <p className="text-lg font-bold text-slate-800">
                  {encounters.filter(e => e.status === 'awaiting_review').length}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center">
                <AlertTriangle className="w-4 h-4 text-red-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Acil Bayrak</p>
                <p className="text-lg font-bold text-slate-800">
                  {encounters.filter(e => e.latest_flag_severity === 'urgent' || e.latest_flag_severity === 'high').length}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center">
                <CheckCircle className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Tamamlanan</p>
                <p className="text-lg font-bold text-slate-800">
                  {encounters.filter(e => e.status === 'reviewed' || e.status === 'closed').length}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* List */}
        <Card className="border-0 shadow-lg">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-6 h-6 animate-spin text-[#5A67D8]" />
              </div>
            ) : encounters.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                <Activity className="w-12 h-12 mb-3 opacity-30" />
                <p>Henuz vaka olusturulmadi</p>
                <Button
                  variant="link"
                  onClick={() => navigate('/doctor/womens-health/new')}
                  className="mt-2 text-[#5A67D8]"
                >
                  Ilk degerlendirmeyi olustur
                </Button>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {encounters.map((enc) => {
                  const statusInfo = STATUS_MAP[enc.status] || STATUS_MAP.draft;
                  return (
                    <button
                      key={enc.id}
                      onClick={() => navigate(`/doctor/womens-health/${enc.id}`)}
                      className="w-full flex items-center gap-4 px-5 py-4 hover:bg-slate-50 transition-colors text-left"
                      data-testid={`encounter-row-${enc.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium text-slate-800 truncate">{enc.patient_name || 'Hasta'}</p>
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                            {TYPE_MAP[enc.encounter_type] || enc.encounter_type}
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-400 truncate">
                          {enc.chief_complaint || 'Sikayet belirtilmedi'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {enc.flag_count > 0 && (
                          <Badge className={`text-[10px] ${SEVERITY_COLORS[enc.latest_flag_severity] || ''}`}>
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            {enc.flag_count}
                          </Badge>
                        )}
                        <Badge className={`text-[10px] ${statusInfo.color}`}>{statusInfo.label}</Badge>
                        <span className="text-xs text-slate-400 w-20 text-right">
                          {new Date(enc.created_at).toLocaleDateString('tr-TR')}
                        </span>
                        <ChevronRight className="w-4 h-4 text-slate-300" />
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
